import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { RefreshCw, AlertCircle, Eye, EyeOff, MapPin, Shield, Download, Upload } from 'lucide-react';
import { useData } from '../contexts/DataContext';

interface SettingsForm {
  // Solar System Properties
  lat: number;
  lng: number;
  timezone: string;
  solcast_forecast_fudge_factor: number;
  max_grid_export_rate: number;
  grid_export_control_enabled: boolean;
  grid_export_battery_full_threshold: number;
  
  // Battery Properties
  max_battery_charge_rate: number;
  max_battery_discharge_rate: number;
  battery_min: number;
  inverter_control_enabled: boolean;
  
  // API Credentials
  amber_api_key: string;
  solarweb_username: string;
  solarweb_password: string;
  // Solcast
  solcast_username: string;
  solcast_password: string;
  solcast_totp_secret: string;
  solcast_pv_system_id: string;
  
  // Fronius Inverter Settings
  fronius_inverter_use_local: boolean;
  fronius_inverter_password: string;
  fronius_inverter_technician_password: string;
  fronius_inverter_local_ip: string;

  // Goals
  battery_reserve_objective: number;
  min_profit_cents: number;
  charge_low_price_threshold: number;
  
  chart_history_hours: number;
  realtime_data_timeout_minutes: number;
}

const SettingsPage: React.FC = () => {
  const { selectedDataId } = useData();
  const queryClient = useQueryClient();
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  
  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiService.getSettings(),
  });

  const { data: settingsComplete } = useQuery({
    queryKey: ['settingsComplete'],
    queryFn: () => apiService.checkSettingsComplete(),
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const missingSettings = new Set(settingsComplete?.missing_settings || []);
  
  const isSettingMissing = (backendKey: string): boolean => {
    return missingSettings.has(backendKey);
  };

  const {
    register,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SettingsForm>({
    defaultValues: {
      lat: settings?.settings?.lat || 0,
      lng: settings?.settings?.lng || 0,
      timezone: settings?.settings?.timezone || '',
      solcast_forecast_fudge_factor: settings?.settings?.solcast_forecast_fudge_factor || 100,
      max_grid_export_rate: settings?.settings?.max_grid_export_rate || 1000,
      grid_export_control_enabled: settings?.settings?.grid_export_control_enabled !== undefined ? settings.settings.grid_export_control_enabled : false,
      grid_export_battery_full_threshold: settings?.settings?.grid_export_battery_full_threshold || 95.0,
      max_battery_charge_rate: settings?.settings?.max_battery_charge_rate || 5000,
      max_battery_discharge_rate: settings?.settings?.max_battery_discharge_rate || 5000,
      battery_min: settings?.settings?.battery_min || 0.05,
      inverter_control_enabled: settings?.settings?.INVERTER_CONTROL_ENABLED !== undefined ? settings.settings.INVERTER_CONTROL_ENABLED : true,
      amber_api_key: settings?.settings?.amber_api_key || '',
      solarweb_username: settings?.settings?.solarweb_username || '',
      solarweb_password: settings?.settings?.solarweb_password || '',
      // Solcast
      solcast_username: settings?.settings?.solcast_username || '',
      solcast_password: settings?.settings?.solcast_password || '',
      solcast_totp_secret: settings?.settings?.solcast_totp_secret || '',
      solcast_pv_system_id: settings?.settings?.solcast_pv_system_id || '',
      fronius_inverter_use_local: settings?.settings?.FRONIUS_INVERTER_USE_LOCAL !== undefined ? settings.settings.FRONIUS_INVERTER_USE_LOCAL : true,
      fronius_inverter_password: settings?.settings?.fronius_inverter_password || '',
      fronius_inverter_technician_password: settings?.settings?.FRONIUS_INVERTER_TECHNICIAN_PASSWORD || '',
      fronius_inverter_local_ip: settings?.settings?.FRONIUS_INVERTER_LOCAL_IP || '',
      battery_reserve_objective: settings?.settings?.battery_reserve_objective !== undefined ? settings.settings.battery_reserve_objective : 0,
      min_profit_cents: settings?.settings?.min_profit_cents || 10,
      charge_low_price_threshold: settings?.settings?.charge_low_price_threshold !== undefined ? settings.settings.charge_low_price_threshold : 25,
      chart_history_hours: settings?.settings?.chart_history_hours !== undefined ? settings.settings.chart_history_hours : 12,
      realtime_data_timeout_minutes: settings?.settings?.realtime_data_timeout_minutes !== undefined ? settings.settings.realtime_data_timeout_minutes : 10,
    }
  });

  // Watch the fronius_inverter_use_local value for conditional validation
  const froniusInverterUseLocal = watch('fronius_inverter_use_local');
  const froniusInverterLocalIp = watch('fronius_inverter_local_ip');

  // Update form when settings data loads
  React.useEffect(() => {
    if (settings?.settings) {
      reset({
        lat: settings.settings.LATITUDE || 0,
        lng: settings.settings.LONGITUDE || 0,
        timezone: settings.settings.TIMEZONE || '',
        solcast_forecast_fudge_factor: settings.settings.SOLCAST_FORECAST_FUDGE_FACTOR !== undefined ? settings.settings.SOLCAST_FORECAST_FUDGE_FACTOR : 100,
        grid_export_control_enabled: settings.settings.GRID_EXPORT_CONTROL_ENABLED !== undefined ? settings.settings.GRID_EXPORT_CONTROL_ENABLED : false,
        grid_export_battery_full_threshold: settings.settings.GRID_EXPORT_BATTERY_FULL_THRESHOLD !== undefined ? settings.settings.GRID_EXPORT_BATTERY_FULL_THRESHOLD : 95.0,
        max_battery_charge_rate: settings.settings.MAX_BATTERY_CHARGE_RATE !== undefined ? settings.settings.MAX_BATTERY_CHARGE_RATE : 9500,
        max_battery_discharge_rate: settings.settings.MAX_BATTERY_DISCHARGE_RATE !== undefined ? settings.settings.MAX_BATTERY_DISCHARGE_RATE : 5000,
        max_grid_export_rate: settings.settings.MAX_GRID_EXPORT_RATE !== undefined ? settings.settings.MAX_GRID_EXPORT_RATE : 1000,
        battery_min: settings.settings.BATTERY_MIN !== undefined ? settings.settings.BATTERY_MIN : 0.05,
        inverter_control_enabled: settings.settings.INVERTER_CONTROL_ENABLED !== undefined ? settings.settings.INVERTER_CONTROL_ENABLED : true,
        amber_api_key: settings.settings.AMBER_API_KEY || '',
        solarweb_username: settings.settings.SOLARWEB_USER || '',
        solarweb_password: settings.settings.SOLARWEB_PASSWORD || '',
        // Solcast
        solcast_username: settings.settings.SOLCAST_USERNAME || '',
        solcast_password: settings.settings.SOLCAST_PASSWORD || '',
        solcast_totp_secret: settings.settings.SOLCAST_TOTP_SECRET || '',
        solcast_pv_system_id: settings.settings.SOLCAST_PV_SYSTEM_ID || '',
        fronius_inverter_use_local: settings.settings.FRONIUS_INVERTER_USE_LOCAL !== undefined ? settings.settings.FRONIUS_INVERTER_USE_LOCAL : true,
        fronius_inverter_password: settings.settings.FRONIUS_INVERTER_PASSWORD || '',
        fronius_inverter_technician_password: settings.settings.FRONIUS_INVERTER_TECHNICIAN_PASSWORD || '',
        fronius_inverter_local_ip: settings.settings.FRONIUS_INVERTER_LOCAL_IP || '',
        battery_reserve_objective: settings.settings.BATTERY_RESERVE_OBJECTIVE !== undefined ? settings.settings.BATTERY_RESERVE_OBJECTIVE : 0,
        min_profit_cents: settings.settings.MIN_PROFIT_CENTS !== undefined ? settings.settings.MIN_PROFIT_CENTS : 10,
        charge_low_price_threshold: settings.settings.CHARGE_LOW_PRICE_THRESHOLD !== undefined ? settings.settings.CHARGE_LOW_PRICE_THRESHOLD : 25,
        chart_history_hours: settings.settings.CHART_HISTORY_HOURS !== undefined ? settings.settings.CHART_HISTORY_HOURS : 12,
        realtime_data_timeout_minutes: settings.settings.REALTIME_DATA_TIMEOUT_MINUTES !== undefined ? settings.settings.REALTIME_DATA_TIMEOUT_MINUTES : 10,
      });
    }
  }, [settings, reset]);

  const updateSettingMutation = useMutation({
    mutationFn: ({ key, setting }: { key: string; setting: any }) =>
      apiService.updateSetting(key, setting),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update setting');
    },
  });

  // Auto-disable toggle if Local IP Address is cleared
  React.useEffect(() => {
    if (froniusInverterUseLocal) {
      const localIpEmpty = !froniusInverterLocalIp || froniusInverterLocalIp.trim() === '';
      
      if (localIpEmpty) {
        // Turn off the toggle if Local IP Address is empty
        setValue('fronius_inverter_use_local', false);
        // Also update the setting via generic API endpoint
        updateSettingMutation.mutate(
          { key: 'FRONIUS_INVERTER_USE_LOCAL', setting: { value: false } },
          {
            onError: (error: any) => {
              console.error('Error disabling local connection:', error);
            }
          }
        );
      }
    }
  }, [froniusInverterLocalIp, froniusInverterUseLocal, setValue, updateSettingMutation]);

  // Auto-save function for individual field changes
  const autoSaveSetting = async (frontendKey: string, value: any) => {
    try {
      const backendKey = keyMapping[frontendKey] || frontendKey;
      
      // Process value based on type
      let processedValue = value;
      if (['solcast_forecast_fudge_factor', 'battery_reserve_objective', 'min_profit_cents', 'charge_low_price_threshold', 'max_battery_charge_rate', 'max_battery_discharge_rate', 'max_grid_export_rate', 'battery_min', 'chart_history_hours', 'realtime_data_timeout_minutes', 'grid_export_battery_full_threshold', 'lat', 'lng'].includes(frontendKey)) {
        processedValue = Number(value);
      } else if (frontendKey === 'inverter_control_enabled' || frontendKey === 'grid_export_control_enabled' || frontendKey === 'fronius_inverter_use_local') {
        processedValue = value === "on" || value === true;
      }
      
      // Skip empty optional fields
      if (['battery_reserve_objective', 'min_profit_cents', 'charge_low_price_threshold', 'grid_export_battery_full_threshold'].includes(frontendKey)) {
        if (value === 0 || value === '' || value === null || value === undefined) {
          return;
        }
      }
      
      // Handle grid export control - call enable/disable endpoints which handle both setting and scheduler
      if (frontendKey === 'grid_export_control_enabled') {
        const currentSetting = settings?.settings?.GRID_EXPORT_CONTROL_ENABLED;
        if (processedValue !== currentSetting) {
          try {
            if (processedValue) {
              await apiService.enableGridExportControl();
              toast.success('Grid export control enabled!');
            } else {
              await apiService.disableGridExportControl();
              toast.success('Grid export control disabled!');
            }
            queryClient.invalidateQueries({ queryKey: ['gridExportStatus'] });
            queryClient.invalidateQueries({ queryKey: ['settings'] });
          } catch (error: any) {
            console.error('Grid export control error:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Failed to update grid export control';
            toast.error(`Grid export control error: ${errorMessage}`);
            
            // Reset the toggle back to the previous state if enable failed
            if (processedValue) {
              setValue('grid_export_control_enabled', false);
            }
          }
        }
      } else if (frontendKey === 'fronius_inverter_use_local') {
        // Validate Local IP Address before enabling
        if (processedValue) {
          const localIp = watch('fronius_inverter_local_ip');
          
          if (!localIp || localIp.trim() === '') {
            toast.error('Inverter Local IP Address is required when using local connection');
            setValue('fronius_inverter_use_local', false);
            return;
          }
        }
        
        // Use the generic settings endpoint like other settings
        await updateSettingMutation.mutateAsync({ key: backendKey, setting: { value: processedValue } });
      } else {
        // Save the setting for all other fields
        await updateSettingMutation.mutateAsync({ key: backendKey, setting: { value: processedValue } });
      }
      
      // Trigger forecast recalculation for Goals settings
      const isGoalsSetting = ['battery_reserve_objective', 'min_profit_cents', 'charge_low_price_threshold'].includes(frontendKey);
      if (isGoalsSetting) {
        try {
          if (selectedDataId) {
            await apiService.recalculateForecast(selectedDataId);
            
            // Immediately refetch relevant queries to refresh the dashboard
            await Promise.all([
              queryClient.refetchQueries({ queryKey: ['chartData'] }),
              queryClient.invalidateQueries({ queryKey: ['dataEntries'] }),
              queryClient.invalidateQueries({ queryKey: ['batteryControlSchedules'] }),
              queryClient.refetchQueries({ queryKey: ['batteryControlScheduleRecommendations'] }),
            ]);
            
            toast.success('Forecast recalculated with updated Goals!');
          } else {
            console.log('No data entry selected, skipping forecast recalculation');
          }
        } catch (forecastError: any) {
          console.error('Forecast recalculation error:', forecastError);
          const errorMsg = forecastError?.response?.data?.detail || forecastError?.message || 'Settings saved but forecast recalculation failed';
          toast.error(errorMsg);
        }
      }
      
    } catch (error: any) {
      console.error('Auto-save error:', error);
      toast.error(`Failed to save ${frontendKey}`);
    }
  };

  // Helper function to create auto-save register
  const autoRegister = (name: keyof SettingsForm, options: any = {}) => {
    const registration = register(name, options);
    return {
      ...registration,
      onBlur: (e: any) => {
        registration.onBlur(e); // Call original onBlur if exists
        if (typeof options.onChange === 'function') {
          // Skip onBlur auto-save for fields with onChange (like checkboxes)
          return;
        }
        autoSaveSetting(name as string, e.target.value);
      }
    };
  };

  const resetSettingsMutation = useMutation({
    mutationFn: () => apiService.resetSettings(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Settings reset successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reset settings');
    },
  });

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Map frontend keys to backend keys
  const keyMapping: Record<string, string> = {
    lat: 'LATITUDE',
    lng: 'LONGITUDE',
    timezone: 'TIMEZONE',
    solcast_forecast_fudge_factor: 'SOLCAST_FORECAST_FUDGE_FACTOR',
    grid_export_control_enabled: 'GRID_EXPORT_CONTROL_ENABLED',
    grid_export_battery_full_threshold: 'GRID_EXPORT_BATTERY_FULL_THRESHOLD',
    max_battery_charge_rate: 'MAX_BATTERY_CHARGE_RATE',
    max_battery_discharge_rate: 'MAX_BATTERY_DISCHARGE_RATE',
    max_grid_export_rate: 'MAX_GRID_EXPORT_RATE',
    battery_min: 'BATTERY_MIN',
    inverter_control_enabled: 'INVERTER_CONTROL_ENABLED',
    amber_api_key: 'AMBER_API_KEY',
    solarweb_username: 'SOLARWEB_USER',
    solarweb_password: 'SOLARWEB_PASSWORD',
    // Solcast
    solcast_username: 'SOLCAST_USERNAME',
    solcast_password: 'SOLCAST_PASSWORD',
    solcast_totp_secret: 'SOLCAST_TOTP_SECRET',
    solcast_pv_system_id: 'SOLCAST_PV_SYSTEM_ID',
    fronius_inverter_use_local: 'FRONIUS_INVERTER_USE_LOCAL',
    fronius_inverter_password: 'FRONIUS_INVERTER_PASSWORD',
    fronius_inverter_technician_password: 'FRONIUS_INVERTER_TECHNICIAN_PASSWORD',
    fronius_inverter_local_ip: 'FRONIUS_INVERTER_LOCAL_IP',
    battery_reserve_objective: 'BATTERY_RESERVE_OBJECTIVE',
    min_profit_cents: 'MIN_PROFIT_CENTS',
    charge_low_price_threshold: 'CHARGE_LOW_PRICE_THRESHOLD',
    chart_history_hours: 'CHART_HISTORY_HOURS',
    realtime_data_timeout_minutes: 'REALTIME_DATA_TIMEOUT_MINUTES',
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values? This action cannot be undone.')) {
      resetSettingsMutation.mutate();
    }
  };

  const handleExportSettings = async () => {
    setIsExporting(true);
    try {
      const blob = await apiService.exportSettings();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `solar-battery-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Settings exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export settings');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportSettings = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      toast.error('Please select a valid JSON file');
      return;
    }

    setIsImporting(true);
    try {
      await apiService.importSettings(file);
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Settings imported successfully!');
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import settings');
    } finally {
      setIsImporting(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const getValidationStatus = () => {
    const requiredFields = ['LATITUDE', 'LONGITUDE', 'TIMEZONE'];
    const hasRequired = requiredFields.every(field => 
      settings?.settings?.[field] && settings.settings[field] !== ''
    );
    
    const hasCredentials = settings?.settings?.AMBER_API_KEY || 
                          (settings?.settings?.SOLARWEB_USER && settings?.settings?.SOLARWEB_PASSWORD);
    
    return { hasRequired, hasCredentials, isComplete: hasRequired && hasCredentials };
  };

  const validationStatus = getValidationStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-3">
          {/* Left side content can be added here if needed */}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExportSettings}
            disabled={isExporting}
            className="btn-secondary flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{isExporting ? 'Exporting...' : 'Export Settings'}</span>
          </button>
          
          <label className="btn-secondary flex items-center space-x-2 cursor-pointer">
            <Upload className="h-4 w-4" />
            <span>{isImporting ? 'Importing...' : 'Import Settings'}</span>
            <input
              type="file"
              accept=".json"
              onChange={handleImportSettings}
              className="hidden"
              disabled={isImporting}
            />
          </label>
          
          <button
            onClick={handleReset}
            disabled={resetSettingsMutation.isPending}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reset All</span>
          </button>
        </div>
      </div>


      <div className="space-y-6">
        {/* View Options */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <Eye className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900">View Options</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chart History (hours)
              </label>
              <input
                type="number"
                min="1"
                max="24"
                {...autoRegister('chart_history_hours', { 
                  min: { value: 1, message: 'Chart history must be at least 1 hour' },
                  max: { value: 24, message: 'Chart history cannot exceed 24 hours' }
                })}
                className={`input ${errors.chart_history_hours ? 'border-red-500' : ''}`}
                placeholder="e.g., 12"
              />
              <p className="mt-1 text-xs text-gray-500">
                Number of hours of historical data to display in charts. Data older than this will be filtered out on the dashboard.
              </p>
              {errors.chart_history_hours && (
                <p className="mt-1 text-sm text-red-600">{errors.chart_history_hours.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Realtime Data Timeout (minutes)
              </label>
              <input
                type="number"
                min="5"
                max="60"
                {...autoRegister('realtime_data_timeout_minutes', { 
                  min: { value: 5, message: 'Timeout must be at least 5 minutes' },
                  max: { value: 60, message: 'Timeout cannot exceed 60 minutes' }
                })}
                className={`input ${errors.realtime_data_timeout_minutes ? 'border-red-500' : ''}`}
                placeholder="e.g., 10"
              />
              <p className="mt-1 text-xs text-gray-500">
                Realtime inverter data fetching will continue for this long after started.
              </p>
              {errors.realtime_data_timeout_minutes && (
                <p className="mt-1 text-sm text-red-600">{errors.realtime_data_timeout_minutes.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Solar System Properties */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <MapPin className="h-5 w-5 text-solar-500" />
            <h2 className="text-lg font-semibold text-gray-900">Solar System Properties</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isSettingMissing('LATITUDE') ? 'text-red-600' : 'text-gray-700'}`}>
                Latitude
              </label>
              <input
                type="number"
                step="0.000001"
                {...autoRegister('lat', { 
                  required: 'Latitude is required',
                  min: { value: -90, message: 'Latitude must be between -90 and 90' },
                  max: { value: 90, message: 'Latitude must be between -90 and 90' }
                })}
                className={`input ${errors.lat ? 'border-red-500' : ''}`}
                placeholder="e.g., -33.8688"
              />
              {errors.lat && (
                <p className="mt-1 text-sm text-red-600">{errors.lat.message}</p>
              )}
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-1 ${isSettingMissing('LONGITUDE') ? 'text-red-600' : 'text-gray-700'}`}>
                Longitude
              </label>
              <input
                type="number"
                step="0.000001"
                {...autoRegister('lng', { 
                  required: 'Longitude is required',
                  min: { value: -180, message: 'Longitude must be between -180 and 180' },
                  max: { value: 180, message: 'Longitude must be between -180 and 180' }
                })}
                className={`input ${errors.lng ? 'border-red-500' : ''}`}
                placeholder="e.g., 151.2093"
              />
              {errors.lng && (
                <p className="mt-1 text-sm text-red-600">{errors.lng.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <input
                type="text"
                {...autoRegister('timezone', { 
                  required: 'Timezone is required',
                  pattern: {
                    value: /^[A-Za-z_]+\/[A-Za-z_]+$/,
                    message: 'Please use format like "Australia/Melbourne"'
                  }
                })}
                className={`input ${errors.timezone ? 'border-red-500' : ''}`}
                placeholder="e.g., Australia/Melbourne"
              />
              {errors.timezone && (
                <p className="mt-1 text-sm text-red-600">{errors.timezone.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Solcast Forecast Fudge Factor (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="50"
                max="150"
                {...autoRegister('solcast_forecast_fudge_factor', {
                  min: { value: 50, message: 'Fudge factor must be between 50 and 150' },
                  max: { value: 150, message: 'Fudge factor must be between 50 and 150' }
                })}
                className={`input ${errors.solcast_forecast_fudge_factor ? 'border-red-500' : ''}`}
                placeholder="e.g., 100"
              />
              <p className="mt-1 text-xs text-gray-500">
                Factor to apply to Solcast predictions, based on observation (default: 100%).
              </p>
              {errors.solcast_forecast_fudge_factor && (
                <p className="mt-1 text-sm text-red-600">{errors.solcast_forecast_fudge_factor.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Grid Export Rate (W)
              </label>
              <input
                type="number"
                min="0"
                {...autoRegister('max_grid_export_rate', { 
                  min: { value: 0, message: 'Grid export rate must be positive' },
                  validate: (value: any) => Number.isInteger(Number(value)) || 'Grid export rate must be a whole number'
                })}
                className={`input ${errors.max_grid_export_rate ? 'border-red-500' : ''}`}
                placeholder="e.g., 1000"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum rate at which power you have been approved to exported to the grid in watts.
              <p className="mt-1 text-xs text-gray-500">
              </p>
                DO NOT INCREASE THIS TO HIGHER THAN WHAT YOU ARE APPROVED FOR
              </p>
              {errors.max_grid_export_rate && (
                <p className="mt-1 text-sm text-red-600">{errors.max_grid_export_rate.message}</p>
              )}
            </div>
          </div>
          
          {/* Grid Export Control Settings */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="space-y-4">
              {/* Grid Export Control Toggle - Half Width with Blank */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-900">
                      Grid Export Control
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Limit power when feed-in tariff is negative
                    </p>
                  </div>
                  <div className="ml-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        {...autoRegister('grid_export_control_enabled', {
                          onChange: (e: any) => autoSaveSetting('grid_export_control_enabled', e.target.checked)
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
                <div></div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Battery Full Threshold (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    {...autoRegister('grid_export_battery_full_threshold', {
                      min: { value: 0, message: 'Threshold must be positive' },
                      max: { value: 100, message: 'Threshold cannot exceed 100%' }
                    })}
                    className={`input ${errors.grid_export_battery_full_threshold ? 'border-red-500' : ''}`}
                    placeholder="e.g., 95.0"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Battery SOC considered "full" for export limiting
                  </p>
                  {errors.grid_export_battery_full_threshold && (
                    <p className="mt-1 text-sm text-red-600">{errors.grid_export_battery_full_threshold.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Inverter Properties */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">Inverter Properties</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fronius Use Local Connection Toggle - Half Width with Blank */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-900">
                  Use Local Fronius Inverter Connection
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  When enabled, connects directly to the inverter via local IP. When disabled, connects via the Solar Web site (remote connection).
                </p>
              </div>
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    {...autoRegister('fronius_inverter_use_local', {
                      onChange: (e: any) => autoSaveSetting('fronius_inverter_use_local', e.target.checked)
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            <div></div>

            {/* Fronius Local IP Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inverter Local IP Address
                {froniusInverterUseLocal && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type="text"
                {...autoRegister('fronius_inverter_local_ip', {
                  required: froniusInverterUseLocal ? 'Inverter Local IP Address is required when using local connection' : false,
                  validate: (value: string) => {
                    if (froniusInverterUseLocal && (!value || value.trim() === '')) {
                      return 'Inverter Local IP Address is required when using local connection';
                    }
                    return true;
                  }
                })}
                className={`input ${errors.fronius_inverter_local_ip ? 'border-red-500' : ''}`}
                placeholder="192.168.1.100"
              />
              <p className="mt-1 text-xs text-gray-500">
                Local network IP address of the Fronius inverter (only used when "Use Local Connection" is enabled)
              </p>
              {errors.fronius_inverter_local_ip && (
                <p className="mt-1 text-sm text-red-600">{errors.fronius_inverter_local_ip.message}</p>
              )}
            </div>

            {/* Fronius Inverter Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fronius Inverter Password
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswords.fronius_inverter_password ? 'text' : 'password'}
                  {...autoRegister('fronius_inverter_password', {
                    required: 'Fronius Inverter Password is required',
                    validate: (value: string) => {
                      if (!value || value.trim() === '') {
                        return 'Fronius Inverter Password is required';
                      }
                      return true;
                    }
                  })}
                  className={`input pr-10 ${errors.fronius_inverter_password ? 'border-red-500' : ''}`}
                  placeholder="Enter Fronius inverter password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility('fronius_inverter_password')}
                >
                  {showPasswords.fronius_inverter_password ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Password for accessing your Fronius inverter as Customer.
              </p>
              {errors.fronius_inverter_password && (
                <p className="mt-1 text-sm text-red-600">{errors.fronius_inverter_password.message}</p>
              )}
            </div>

            {/* Fronius Inverter Technician Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fronius Inverter Technician Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.fronius_inverter_technician_password ? 'text' : 'password'}
                  {...autoRegister('fronius_inverter_technician_password')}
                  className={`input pr-10 ${errors.fronius_inverter_technician_password ? 'border-red-500' : ''}`}
                  placeholder="Enter Fronius inverter technician password (optional)"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility('fronius_inverter_technician_password')}
                >
                  {showPasswords.fronius_inverter_technician_password ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Optional technician password for accessing advanced Fronius inverter settings.
              </p>
              {errors.fronius_inverter_technician_password && (
                <p className="mt-1 text-sm text-red-600">{errors.fronius_inverter_technician_password.message}</p>
              )}
            </div>

          </div>
        </div>

        {/* Battery Properties */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">Battery Properties</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Battery Charge Rate (W)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                {...autoRegister('max_battery_charge_rate', { 
                  min: { value: 0, message: 'Charge rate must be positive' }
                })}
                className={`input ${errors.max_battery_charge_rate ? 'border-red-500' : ''}`}
                placeholder="e.g., 9.5"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum rate at which the battery can be charged in kilowatts.
              </p>
              {errors.max_battery_charge_rate && (
                <p className="mt-1 text-sm text-red-600">{errors.max_battery_charge_rate.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Battery Discharge Rate (W)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                {...autoRegister('max_battery_discharge_rate', { 
                  min: { value: 0, message: 'Discharge rate must be positive' }
                })}
                className={`input ${errors.max_battery_discharge_rate ? 'border-red-500' : ''}`}
                placeholder="e.g., 9.5"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum rate at which the battery can be discharged in kilowatts.
              </p>
              {errors.max_battery_discharge_rate && (
                <p className="mt-1 text-sm text-red-600">{errors.max_battery_discharge_rate.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Battery Level %
              </label>
              <input
                type="number"
                step="1"
                min="1"
                {...autoRegister('battery_min', { 
                  min: { value: 0, message: 'Battery minimum must be positive' }
                })}
                className={`input ${errors.battery_min ? 'border-red-500' : ''}`}
                placeholder="e.g., 5"
              />
              <p className="mt-1 text-xs text-gray-500">
                Minimum battery level % before the system stops discharging - see SOC Settings on the inverter.
              </p>
              {errors.battery_min && (
                <p className="mt-1 text-sm text-red-600">{errors.battery_min.message}</p>
              )}
            </div>
            
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...autoRegister('inverter_control_enabled', {
                    onChange: (e: any) => autoSaveSetting('inverter_control_enabled', e.target.checked)
                  })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Enable Automatic InverterControlToggle
                  </label>
                  <p className="text-xs text-gray-500">
                    When enabled, the system will automatically update battery control schedules based on forecasts. 
                    Manual execution via "Execute Battery" button will always override this setting.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Credentials */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <Shield className="h-5 w-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900">API Credentials</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isSettingMissing('SOLARWEB_USER') ? 'text-red-600' : 'text-gray-700'}`}>
                SolarWeb Username
              </label>
              <input
                type="text"
                {...autoRegister('solarweb_username')}
                className="input"
                placeholder="Enter SolarWeb username"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-1 ${isSettingMissing('SOLARWEB_PASSWORD') ? 'text-red-600' : 'text-gray-700'}`}>
                SolarWeb Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.solarweb_password ? 'text' : 'password'}
                  {...autoRegister('solarweb_password')}
                  className="input pr-10"
                  placeholder="Enter SolarWeb password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility('solarweb_password')}
                >
                  {showPasswords.solarweb_password ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Solcast Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Solcast Username
              </label>
              <input
                type="text"
                {...autoRegister('solcast_username')}
                className="input"
                placeholder="Enter Solcast username"
              />
            </div>

            {/* Solcast Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Solcast Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.solcast_password ? 'text' : 'password'}
                  {...autoRegister('solcast_password')}
                  className="input pr-10"
                  placeholder="Enter Solcast password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility('solcast_password')}
                >
                  {showPasswords.solcast_password ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Solcast TOTP Secret */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Solcast TOTP Secret
              </label>
              <div className="relative">
                <input
                  type={showPasswords.solcast_totp_secret ? 'text' : 'password'}
                  {...autoRegister('solcast_totp_secret')}
                  className="input pr-10"
                  placeholder="Base32 secret from authenticator setup"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility('solcast_totp_secret')}
                >
                  {showPasswords.solcast_totp_secret ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Authenticator base32 secret (not the 6-digit code). Required if Solcast 2FA is enabled.
              </p>
            </div>

            {/* Solcast PV System ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Solcast PV System ID
              </label>
              <input
                type="text"
                {...autoRegister('solcast_pv_system_id')}
                className="input"
                placeholder="Only needed if Solcast has multiple system"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isSettingMissing('AMBER_API_KEY') ? 'text-red-600' : 'text-gray-700'}`}>
                Amber API Key
              </label>
              <div className="relative">
                <input
                  type={showPasswords.amber_api_key ? 'text' : 'password'}
                  {...autoRegister('amber_api_key')}
                  className="input pr-10"
                  placeholder="Enter Amber API key"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility('amber_api_key')}
                >
                  {showPasswords.amber_api_key ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Goals */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">Goals</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Battery Reserve Objective (kWh)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                {...autoRegister('battery_reserve_objective', { 
                  min: { value: 0, message: 'Battery reserve must be positive' }
                })}
                className={`input ${errors.battery_reserve_objective ? 'border-red-500' : ''}`}
                placeholder="e.g., 5.0"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional. Target battery reserve level in kWh to maintain for backup power.
              </p>
              {errors.battery_reserve_objective && (
                <p className="mt-1 text-sm text-red-600">{errors.battery_reserve_objective.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Profit for Discharge (cents)
              </label>
              <input
                type="number"
                step="0.1"
                {...autoRegister('min_profit_cents')}
                className={`input ${errors.min_profit_cents ? 'border-red-500' : ''}`}
                placeholder="e.g., 10"
              />
              <p className="mt-1 text-xs text-gray-500">
                Minimum profit margin in cents required before discharging battery to grid. Negative values allow discharging even at a loss.
              </p>
              {errors.min_profit_cents && (
                <p className="mt-1 text-sm text-red-600">{errors.min_profit_cents.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Charge Low Price Threshold (cents/kWh)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                {...autoRegister('charge_low_price_threshold', { 
                  min: { value: 0, message: 'Threshold must be positive' }
                })}
                className={`input ${errors.charge_low_price_threshold ? 'border-red-500' : ''}`}
                placeholder="e.g., 25"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum price in cents/kWh at which the system will charge the battery from the grid.
              </p>
              {errors.charge_low_price_threshold && (
                <p className="mt-1 text-sm text-red-600">{errors.charge_low_price_threshold.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Auto-save indicator */}
        <div className="text-center text-sm text-gray-500">
          <p>Changes are saved automatically when you leave each field</p>
        </div>
      </div>

      {/* Required Settings Warning */}
      {!validationStatus.isComplete && (
        <div className="card border-yellow-200 bg-yellow-50">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Configuration Required
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                Please configure your solar system properties and at least one set of API credentials (Amber, SolarWeb, or Fronius inverter) to start using the services.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage; 